import { ComponentRef, EventEmitter, Injectable, Injector, InputSignal, Type, ViewContainerRef } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { OverlayRef } from './overlay-ref.model';


// TODO
type _GetSignalType<T> = T extends InputSignal<infer U> ? U : never;


export interface OverlayOptions<T> {
    inputs?: any,  // Partial<Record<keyof T, T[keyof T] extends InputSignal<any> ? GetSignalType<T[keyof T]> : T[keyof T]>>;
    outputs?: Partial<Record<keyof T, (event?: unknown) => void>>;
    parentInjector?: Injector;
    projectableNodes?: Node[][];
}


const isEventEmitter = <T>(i: unknown): i is EventEmitter<T> => i !== null && typeof (i as { pipe: unknown }).pipe === 'function';


@Injectable({
    providedIn: 'root',
})
export class OverlaysService {

    overlaysHostRef: ViewContainerRef | null;

    protected overlayStack: OverlayRef[] = [];

    constructor(private injector: Injector) {}

    createOverlay<T, R = any>(componentType: Type<T>, options?: OverlayOptions<T>): OverlayRef<R, T> {
        if (!componentType) {
            throw new Error('componentType is undefined');
        }

        options = options || {};

        const injector = Injector.create({
            providers: [{ provide: OverlayRef, deps: [] }],
            parent: options.parentInjector || this.injector,
        });
        const overlayComponentRef = this.createComponent(componentType, injector, options.projectableNodes);

        const overlayRef = injector.get(OverlayRef);
        overlayRef.componentRef = overlayComponentRef;

        this.bindComponentOutputs(overlayComponentRef, options.outputs || {});
        this.updateComponentInputs(overlayComponentRef, options.inputs || {});
        this.registerOverlayRef(overlayRef);

        return overlayRef;
    }

    getBoundingClientRect() {
        return this.overlaysHostRef?.element.nativeElement.getBoundingClientRect();
    }

    isTopmostOverlay(overlayRef: OverlayRef) {
        return this.overlayStack[this.overlayStack.length - 1] === overlayRef;
    }

    getTopmostOverlay() {
        return this.overlayStack[this.overlayStack.length - 1];
    }

    private createComponent<T>(componentType: Type<T>, injector: Injector, projectableNodes?: Node[][]) {
        if (!this.overlaysHostRef) {
            throw Error('overlaysHostRef is not set, please add "[overlaysTarget]" directive to some host element');
        }
        return this.overlaysHostRef.createComponent(componentType, { index: this.overlaysHostRef.length, injector, projectableNodes });
    }

    private bindComponentOutputs<T>(componentRef: ComponentRef<T>, outputs: Partial<Record<keyof T, () => void>>) {
        const destroyed = new Subject<void>();
        componentRef.onDestroy(() => {
            destroyed.next();
        });
        Object.keys(outputs).forEach(propName => {
            const output = componentRef.instance[propName as keyof T];
            if (output && isEventEmitter(output)) {
                output.pipe(takeUntil(destroyed)).subscribe(outputs[propName as keyof T]);
            }
        });
    }

    private updateComponentInputs<T>(componentRef: ComponentRef<T>, inputs: Partial<T>) {
        Object.keys(inputs).forEach(propName => {
            componentRef.setInput(propName, inputs[propName as keyof T]);
        });
        componentRef.changeDetectorRef.detectChanges();
    }

    private registerOverlayRef(overlayRef: OverlayRef) {
        overlayRef.componentRef.onDestroy(() => {
            const idx = this.overlayStack.indexOf(overlayRef);
            if (idx !== -1) {
                this.overlayStack.splice(idx, 1);
            }
        });
        this.overlayStack.push(overlayRef);
    }
}
